import React from "react";
import { shopLogoUrl } from "../lib/branding";
import { PhotoStrip, galleryFromItem } from "./ItemGallery";
import { money, fmtDate } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Input } from "./ui/input";

function DocHeader({ shop, testid }) {
  return (
    <div className="flex items-center gap-4 border-b-2 border-[var(--brand,#1E3A5F)] pb-3">
      {shop.logo_path ? (
        <img src={shopLogoUrl(shop)} alt={shop.name} data-testid={testid}
          className="h-14 w-14 shrink-0 rounded-full border-2 border-[var(--brand,#1E3A5F)] object-cover" />
      ) : (
        <div className="h-14 w-14 shrink-0 rounded-full border-2 border-[var(--brand,#1E3A5F)] flex items-center justify-center font-heading font-bold text-[var(--brand,#1E3A5F)] text-lg">
          {shop.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("")}
        </div>
      )}
      <div>
        <div className="font-heading font-bold text-xl text-[var(--brand,#1E3A5F)] uppercase tracking-tight">{shop.name}</div>
        {shop.header_text && <div className="text-xs text-slate-600">{shop.header_text}</div>}
        {shop.company_address && <div className="text-xs text-slate-500">{shop.company_address}</div>}
        <div className="text-xs text-slate-500">
          {shop.company_phone && <span>WhatsApp: {shop.company_phone}</span>}
          {shop.owner_email && <span> · Email: {shop.owner_email}</span>}
        </div>
      </div>
    </div>
  );
}

function DocFooter({ shop }) {
  return (
    <div className="border-t border-[var(--brand,#1E3A5F)] mt-6 pt-2 text-[10px] text-slate-500 text-center">
      © {new Date().getFullYear()} {shop.name}. All Rights Reserved.
      {shop.company_address && ` · ${shop.company_address}`}
      {shop.company_phone && ` · WhatsApp: ${shop.company_phone}`}
      {shop.owner_email && ` · Email: ${shop.owner_email}`}
      {shop.footer_text && <div className="mt-1">{shop.footer_text}</div>}
    </div>
  );
}

function PartyRow({ label, lines, testid }) {
  return (
    <div className="border border-slate-200 rounded p-2" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--brand,#1E3A5F)]">{label}</div>
      <div className="text-xs leading-relaxed">{lines.filter(Boolean).join(" · ")}</div>
    </div>
  );
}

function ItemAnnex({ contract }) {
  const { t } = useLang();
  const usd = (v) => `USD $${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  return (
    <div className="mt-3 space-y-3" data-testid="contract-item-annex">
      <div className="font-bold text-[var(--brand,#1E3A5F)] text-xs uppercase">{t("doc.annex1")}</div>
      {contract.items.map((i) => (
        <table key={i.id} className="w-full text-xs">
          <tbody>
            {galleryFromItem(i, contract.loan).length > 0 && (
              <tr className="border-b border-slate-100">
                <td className="py-1 pl-2 font-bold text-slate-600 w-[28%]">Fotografia:</td>
                <td className="py-1"><PhotoStrip item={i} photos={galleryFromItem(i, contract.loan)} size="h-16 w-16" testid={`annex-photos-${i.id.slice(0, 8)}`} /></td>
              </tr>
            )}
            {[
              ["Deskrisaun:", i.description || i.name], ["Kategoria:", i.category],
              ["Brand:", i.brand], ["Model:", i.model], ["Tinan Fabrika:", i.year], ["Kolór:", i.color],
              ["Plate Number:", i.plate_no], ["Chassis / VIN:", i.chassis_no], [t("items.engine") + ":", i.engine_no],
              ["Fuel %:", i.fuel_percent], [t("items.dntt") + ":", i.dntt_ref],
              ["Serial No.:", i.serial_no], [t("items.imei") + ":", i.imei],
              [t("items.backup_done") + ":", i.backup_done == null ? null : (i.backup_done ? "Sim / Yes" : "Lae / No")],
              [t("items.data_wiped") + ":", i.data_wiped == null ? null : (i.data_wiped ? "Sim / Yes" : "Lae / No")],
              ["Jewelry Type:", i.jewelry_type], ["Gold Karat:", i.gold_karat], ["Weight:", i.weight_grams ? `${i.weight_grams} g` : null],
              [t("items.hours") + ":", i.operating_hours], [t("items.maintenance") + ":", i.last_maintenance], [t("items.accessories") + ":", i.accessories],
              ["Kondisaun:", i.condition], ["Fatin:", i.storage_location], ["Valor Indikativu:", usd(i.estimated_value)],
            ].filter(([, v]) => v).map(([l, v], r) => (
              <tr key={r} className="border-b border-slate-100">
                <td className="py-1 pl-2 font-bold text-slate-600 w-[28%]">{l}</td>
                <td className="py-1">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </div>
  );
}

export function ContractDoc({ contract, printMode = "a4", draftMark = false, editing = false, draft = null, setDraftArticle = () => {}, setDraftTerm = () => {} }) {
  const { t } = useLang();
  const usd = (v) => `USD $${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  const tpl = contract.template || {};
  const langs = tpl.langs || ["tet"];
  const parties = contract.parties || {};
  const c = contract.customer || {};
  const shop = contract.shop;
  const m = contract.meta;
  const isCompany = parties.borrower_kind === "company";
  const title = langs.map((l) => tpl.title?.[l]).filter(Boolean);
  const subtitle = langs.map((l) => tpl.subtitle?.[l]).filter(Boolean);
  const penalty = m.penalty_mode === "late_interest" ? `${m.late_rate ?? 0}% / month (mora)` : `${m.penalty_rate}% (cláusula penal)`;
  return (
    <div className={`print-area bg-white text-sm ${printMode === "thermal" ? "thermal-doc" : ""}`} data-testid="contract-body">
      {draftMark && <div className="draft-watermark" data-testid="draft-watermark">DRAFT</div>}
      <DocHeader shop={shop} testid="contract-logo" />
      <div className="text-[10px] text-slate-400 mt-1">Pawn Management System · Lei n.º 6/2025 · {t("ct.template_version")} {tpl.use}/v{tpl.version ?? 0}</div>
      <h2 className="text-center font-heading font-bold text-xl tracking-wide mt-5 mb-1" data-testid="contract-title">{title[0] || "KONTRATU PENHOR"}</h2>
      {title.slice(1).map((x) => <div key={x} className="text-center text-xs font-semibold text-slate-600">{x}</div>)}
      {subtitle.length > 0 && <div className="text-center text-[11px] text-slate-500 mt-1 mb-4">{subtitle.join(" · ")}</div>}
      <table className="w-full text-xs border border-slate-300 mb-3" data-testid="contract-meta-table">
        <tbody>
          {[
            ["Nú Kontratu", contract.contract_no, "Tipo Kontratu", contract.tipo],
            ["Data", m.start, "Status", m.status],
          ].map(([l1, v1, l2, v2], r) => (
            <tr key={r} className="border-b border-slate-200">
              <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] w-[18%] bg-slate-50">{l1}</td>
              <td className="p-2 w-[32%] font-mono">{v1}</td>
              <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] w-[18%] bg-slate-50">{l2}</td>
              <td className="p-2 font-mono">{v2}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="font-bold text-[var(--brand,#1E3A5F)] text-xs uppercase mb-1">{t("doc.parties")}</div>
      <div className="grid gap-2 mb-3">
        <PartyRow label={t("doc.creditor")} testid="contract-party-creditor" lines={[shop.name, shop.license_no && `Lic. ${shop.license_no}`, shop.company_address, shop.company_phone && `Tel. ${shop.company_phone}`, shop.owner_email]} />
        <PartyRow label={`${t("doc.client")} — ${isCompany ? t("ct.company") : t("ct.individual")}`} testid="contract-party-client" lines={[
          c.name, c.id_number && `${c.id_type || "BI"} ${c.id_number}`, c.nif && `NIF ${c.nif}`, c.company_reg_no && `Reg. ${c.company_reg_no}`, c.legal_form,
          c.nationality, c.marital_status, c.profession, [c.address, c.suco, c.posto, c.municipality].filter(Boolean).join(", "), c.phone && `Tel./WhatsApp ${c.phone}`, c.email,
          isCompany && c.rep_name && `${t("doc.rep")}: ${c.rep_name}${c.rep_position ? ` (${c.rep_position})` : ""}${c.rep_id_number ? ` · ID ${c.rep_id_number}` : ""}`,
        ]} />
        {parties.guarantor && <PartyRow label={t("doc.guarantor")} testid="contract-party-guarantor" lines={[parties.guarantor.name, parties.guarantor.role, parties.guarantor.id_number && `ID ${parties.guarantor.id_number}`, parties.guarantor.nif && `NIF ${parties.guarantor.nif}`, parties.guarantor.phone]} />}
        {parties.consenter && <PartyRow label={t("doc.consenter")} testid="contract-party-consenter" lines={[parties.consenter.name, parties.consenter.role, parties.consenter.id_number && `ID ${parties.consenter.id_number}`, parties.consenter.share_percent && `${parties.consenter.share_percent}%`]} />}
      </div>
      <div className="font-bold text-[var(--brand,#1E3A5F)] text-xs uppercase mb-1">{t("doc.conditions")}</div>
      <table className="w-full text-xs border border-slate-300 mb-2" data-testid="contract-conditions-table">
        <tbody>
          {[
            ["Kapitál / Capital", usd(m.principal), "Prinsipál atuál", usd(m.principal_now)],
            ["Taxa juru / Interest", `${m.rate.toFixed(2)}% / fulan`, "Prazu / Term", `${m.term} fulan`],
            ["Hahú / Start", m.start, "Remata / Due", m.due],
            [t("ct.final_maturity"), m.final_maturity, t("ct.penalty_mode"), penalty],
            [t("ct.storage_cost"), m.storage_cost ? `${usd(m.storage_cost)} / ${m.storage_period}` : "—", t("ct.insurance"), m.insurance ? usd(m.insurance) : "—"],
            [t("ct.capital_delivery"), m.capital_delivery || `${m.start} · cash`, t("ct.payment_schedule"), m.payment_schedule || "—"],
            ["Total estimadu / Estimated total", usd(m.total_selu), t("ct.other_costs"), m.other_costs || "—"],
          ].map(([l1, v1, l2, v2], r) => (
            <tr key={r} className="border-b border-slate-200">
              <td className="p-1.5 font-bold text-[var(--brand,#1E3A5F)] w-[20%] bg-slate-50">{l1}</td>
              <td className="p-1.5 w-[30%] font-mono">{v1}</td>
              <td className="p-1.5 font-bold text-[var(--brand,#1E3A5F)] w-[20%] bg-slate-50">{l2}</td>
              <td className="p-1.5 font-mono">{v2}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {contract.articles.map((a) => (
        <div key={a.no} className="mt-4" data-testid={`contract-article-${a.no}`}>
          {editing && draft ? (
            <div className="space-y-1" data-testid={`contract-edit-article-${a.no}`}>
              <Input value={draft.articles.find((x) => x.no === a.no)?.title ?? a.title}
                onChange={(e) => setDraftArticle(a.no, "title", e.target.value)} className="h-8 text-xs font-semibold bg-white" />
              <textarea rows={6} value={draft.articles.find((x) => x.no === a.no)?.body ?? a.body}
                onChange={(e) => setDraftArticle(a.no, "body", e.target.value)}
                className="w-full rounded-md border border-input bg-white px-2 py-1.5 text-xs" />
            </div>
          ) : (
            <>
              <div className="font-bold text-[var(--brand,#1E3A5F)]">Artigu {a.no}º — {a.title}</div>
              <p className="whitespace-pre-line mt-1 text-[12px] leading-relaxed">{a.body}</p>
            </>
          )}
          {a.no === (tpl.items_after_no ?? 5) && <ItemAnnex contract={contract} />}
        </div>
      ))}
      {(contract.additional_terms_en?.length > 0 || editing) && (
        <div className="mt-5">
          {editing && draft ? (
            <div className="space-y-1 mt-1" data-testid="contract-edit-terms">
              {draft.terms.map((term, i) => (
                <textarea key={i} rows={2} value={term} onChange={(e) => setDraftTerm(i, e.target.value)}
                  className="w-full rounded-md border border-input bg-white px-2 py-1 text-xs" />
              ))}
            </div>
          ) : (
            <ul className="text-[11px] italic text-slate-600 space-y-0.5">
              {contract.additional_terms_en.map((term, i) => <li key={i}>{term}</li>)}
            </ul>
          )}
        </div>
      )}
      {contract.custom_terms && (
        <p className="mt-3 text-xs leading-relaxed text-slate-700 whitespace-pre-line border-t border-dashed border-slate-300 pt-2">{contract.custom_terms}</p>
      )}
      <div className={`grid gap-6 mt-14 text-center text-xs ${parties.guarantor || parties.consenter ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`} data-testid="contract-signatures">
        <div>
          <div className="border-t border-slate-500 pt-2 font-semibold">{t("doc.sign_creditor")}</div>
          <div className="text-slate-600 mt-1">{shop.name}</div>
        </div>
        <div>
          <div className="border-t border-slate-500 pt-2 font-semibold">{t("doc.sign_client")}{isCompany ? ` · ${t("ct.company")}` : ""}</div>
          <div className="text-slate-600 mt-1">{isCompany && c.rep_name ? `${c.rep_name} p/ ${c.name}` : c.name}</div>
        </div>
        {parties.guarantor && (
          <div>
            <div className="border-t border-slate-500 pt-2 font-semibold">{t("doc.sign_guarantor")}</div>
            <div className="text-slate-600 mt-1">{parties.guarantor.name}</div>
          </div>
        )}
        {parties.consenter && (
          <div>
            <div className="border-t border-slate-500 pt-2 font-semibold">{t("doc.sign_consenter")}</div>
            <div className="text-slate-600 mt-1">{parties.consenter.name}</div>
          </div>
        )}
        <div>
          <div className="border-t border-slate-500 pt-2 font-semibold">{t("doc.sign_witness")}</div>
          <div className="text-slate-600 mt-1">{parties.witness_name || "Naran: ____________________"}{parties.witness_id ? ` · ${parties.witness_id}` : ""}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-8 mt-8">
        <div className="border border-dashed border-slate-400 rounded-lg h-24 flex items-center justify-center text-[10px] uppercase tracking-wider text-slate-400" data-testid="contract-stamp-area">
          {t("contract.stamp")}{isCompany ? " · Karimbo empreza / Company stamp" : ""}
        </div>
        <div className="border border-dashed border-slate-400 rounded-lg h-24 flex items-center justify-center text-[10px] uppercase tracking-wider text-slate-400" data-testid="contract-thumbprint-area">
          {t("contract.thumbprint")}
        </div>
      </div>
      <DocFooter shop={shop} />
    </div>
  );
}

export function BillDoc({ bill, printMode = "a4", draftMark = false }) {
  const { t } = useLang();
  return (
    <div className={`print-area bg-white text-sm ${printMode === "thermal" ? "thermal-doc" : ""}`} data-testid="bill-body">
      {draftMark && <div className="draft-watermark" data-testid="draft-watermark-bill">DRAFT</div>}
      <DocHeader shop={bill.shop} testid="bill-logo" />
      <h2 className="text-center font-heading font-bold text-xl tracking-wide my-4">{t("bill.title")} · {t("bill.for")}</h2>
      <table className="w-full text-xs border border-slate-300 mb-4">
        <tbody>
          <tr className="border-b border-slate-200">
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50 w-[20%]">No.</td>
            <td className="p-2 font-mono" data-testid="bill-no">{bill.bill_no}</td>
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50 w-[20%]">{t("rcp.contract_no")}</td>
            <td className="p-2 font-mono">CTR-{bill.loan.start_date.slice(0, 4)}-{bill.loan.loan_number.replace("LN-", "")}</td>
          </tr>
          <tr className="border-b border-slate-200">
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50">{t("rcp.client")}</td>
            <td className="p-2">{bill.customer?.name}</td>
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50">{t("loans.due")}</td>
            <td className="p-2 font-mono">{fmtDate(bill.loan.due_date)}</td>
          </tr>
        </tbody>
      </table>
      <table className="w-full text-xs">
        <tbody>
          {[
            [t("rcp.original_loan"), money(bill.loan.principal)],
            [t("rcp.rate"), `${bill.loan.monthly_interest_rate.toFixed(2)}%`],
            [t("rcp.months_billed"), bill.loan.months_billed],
            [t("rcp.interest_charged"), money(bill.loan.interest_accrued)],
            [t("rcp.interest_remaining"), money(bill.loan.interest_due)],
            [t("rcp.principal_remaining"), money(bill.loan.principal_due)],
            [t("rcp.penalty"), money(bill.loan.penalty)],
          ].map(([l, v], r) => (
            <tr key={r} className="border-b border-slate-100">
              <td className="py-1.5 pl-2 text-slate-600">{l}</td>
              <td className="py-1.5 text-right pr-2 font-mono">{v}</td>
            </tr>
          ))}
          <tr className="border-b-2 border-slate-800 font-bold">
            <td className="py-2 pl-2">{t("rcp.total_remaining")}</td>
            <td className="py-2 text-right pr-2 font-mono" data-testid="bill-total">{money(bill.loan.total_due)}</td>
          </tr>
        </tbody>
      </table>
      {bill.loan.next_payment && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mt-4 text-xs space-y-1" data-testid="bill-next-payment">
          <div className="font-bold text-slate-700 mb-1">{t("rcp.next_title")}</div>
          <div className="flex justify-between"><span>{t("rcp.next_date")}</span><span className="font-mono font-semibold">{bill.loan.next_payment.date}</span></div>
          <div className="flex justify-between"><span>{t("rcp.current_balance")}</span><span className="font-mono">{money(bill.loan.total_due)}</span></div>
          {bill.loan.next_payment.type === "interest" && (
            <div className="flex justify-between font-semibold">
              <span>{t("rcp.if_unpaid")}</span>
              <span className="font-mono">{money(bill.loan.next_payment.new_total_if_unpaid)}</span>
            </div>
          )}
        </div>
      )}
      <div className="mt-4">
        <div className="text-xs font-bold text-slate-700 mb-1">{t("rcp.pawn_item")}</div>
        <table className="w-full text-xs border border-slate-300">
          <tbody>
            {bill.items.map((i) => (
              <tr key={i.id} className="border-b border-slate-200">
                <td className="p-2">{i.name}</td>
                <td className="p-2">{i.category}</td>
                <td className="p-2 font-mono">{i.serial_no || i.plate_no || "—"}</td>
                <td className="p-2 text-right font-mono">{money(i.estimated_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DocFooter shop={bill.shop} />
    </div>
  );
}

export function ReceiptDoc({ receipt, printMode = "a4", draftMark = false }) {
  const { t } = useLang();
  return (
    <div className={`print-area bg-white text-sm ${printMode === "thermal" ? "thermal-doc" : ""}`} data-testid="receipt-body">
      {draftMark && <div className="draft-watermark" data-testid="draft-watermark">DRAFT</div>}
      <DocHeader shop={receipt.shop} testid="receipt-logo" />
      <h2 className="text-center font-heading font-bold text-xl tracking-wide my-4">{t("rcp.title")}</h2>
      <table className="w-full text-xs border border-slate-300 mb-3">
        <tbody>
          <tr className="border-b border-slate-200">
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50 w-[18%]">{t("rcp.payment_type")}</td>
            <td className="p-2 w-[32%]">{receipt.payment.payment_type ? t(`pay.type_${receipt.payment.payment_type}`) : "—"}</td>
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50 w-[18%]">{t("rcp.contract_no")}</td>
            <td className="p-2 font-mono">{receipt.loan ? `CTR-${receipt.loan.start_date.slice(0, 4)}-${receipt.loan.loan_number.replace("LN-", "")}` : "—"}</td>
          </tr>
          <tr className="border-b border-slate-200">
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50">{t("rcp.client")}</td>
            <td className="p-2">{receipt.payment.customer_name}</td>
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50">{t("common.date")}</td>
            <td className="p-2 font-mono">{fmtDate(receipt.payment.date)}</td>
          </tr>
        </tbody>
      </table>
      <div className="font-mono text-xs text-slate-500 mb-3" data-testid="receipt-no">{receipt.receipt_no}</div>
      {receipt.loan && (
        <table className="w-full text-xs">
          <tbody>
            {[
              [t("rcp.original_loan"), money(receipt.loan.principal)],
              [t("rcp.rate"), `${receipt.loan.monthly_interest_rate.toFixed(2)}%`],
              [t("rcp.months_billed"), receipt.loan.months_billed],
              [t("rcp.amount_paid"), money(receipt.payment.amount)],
              ...(receipt.payment.penalty_portion ? [[t("rcp.penalty_paid"), money(receipt.payment.penalty_portion)]] : []),
              [t("rcp.principal_remaining"), money(receipt.loan.principal_due)],
              [t("rcp.interest_remaining"), money(receipt.loan.interest_due)],
              [t("rcp.penalty"), money(receipt.loan.penalty)],
            ].map(([l, v], r) => (
              <tr key={r} className="border-b border-slate-100">
                <td className="py-1.5 pl-2 text-slate-600">{l}</td>
                <td className="py-1.5 text-right pr-2 font-mono">{v}</td>
              </tr>
            ))}
            <tr className="border-b-2 border-slate-800 font-bold">
              <td className="py-2 pl-2">{t("rcp.total_remaining")}</td>
              <td className="py-2 text-right pr-2 font-mono" data-testid="receipt-amount">{money(receipt.loan.total_due)}</td>
            </tr>
          </tbody>
        </table>
      )}
      {receipt.loan?.next_payment && (
        <>
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mt-4 text-xs space-y-1" data-testid="receipt-next-payment">
            <div className="font-bold text-slate-700 mb-1">{t("rcp.next_title")}</div>
            <div className="flex justify-between"><span>{t("rcp.next_date")}</span><span className="font-mono font-semibold">{receipt.loan.next_payment.date}</span></div>
            <div className="flex justify-between"><span>{t("rcp.current_balance")}</span><span className="font-mono">{money(receipt.loan.total_due)}</span></div>
            {receipt.loan.next_payment.type === "interest" && (
              <>
                <div className="flex justify-between">
                  <span>{t("rcp.next_interest")}</span>
                  <span className="font-mono">+ {money(receipt.loan.next_payment.amount)} ({receipt.loan.monthly_interest_rate}% × {money(receipt.loan.principal_due)})</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>{t("rcp.if_unpaid")}</span>
                  <span className="font-mono">{money(receipt.loan.next_payment.new_total_if_unpaid)}</span>
                </div>
              </>
            )}
          </div>
          <p className="text-[10px] italic text-slate-500 mt-1.5">
            Favor selu iha loron {receipt.loan.next_payment.date} atu evita interese fulan tan. · Please pay by {receipt.loan.next_payment.date} to avoid another month of interest.
          </p>
        </>
      )}
      {receipt.calc && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4 text-xs space-y-1">
          <div className="font-bold text-slate-700 mb-1">{t("rcp.calc_title")}</div>
          <div className="flex justify-between"><span>{t("rcp.contract_start")}</span><span className="font-mono">{receipt.calc.contract_start}</span></div>
          <div className="flex justify-between"><span>{t("rcp.payment_date")}</span><span className="font-mono">{receipt.calc.payment_date}</span></div>
          <div className="flex justify-between"><span>{t("rcp.billing_months")}</span><span className="font-mono">{receipt.calc.months_billed} months · {receipt.calc.months_billed} fulan</span></div>
          <div className="flex justify-between"><span>{t("rcp.rate_loan")}</span><span className="font-mono">{money(receipt.calc.monthly_interest)}</span></div>
          <div className="flex justify-between font-semibold">
            <span>{t("rcp.interest_charged")}</span>
            <span className="font-mono">{receipt.calc.months_billed} × {money(receipt.calc.monthly_interest)} = {money(receipt.calc.interest_charged)}</span>
          </div>
          <p className="text-[10px] italic text-slate-500 pt-1">{t("rcp.art4_note")}</p>
        </div>
      )}
      {receipt.items?.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-bold text-slate-700 mb-1">{t("rcp.pawn_item")}</div>
          {receipt.items.map((i) => (
            <table key={i.id} className="w-full text-xs mb-2">
              <tbody>
                {[
                  ["Type:", i.category, "Name / Model:", [i.brand, i.model].filter(Boolean).join(" ") || i.name],
                  ["Tinan / Year:", i.year, "Kolór / Color:", i.color],
                  ["Machine No.:", i.serial_no, "Chassis:", i.chassis_no],
                  ["Plate:", i.plate_no, "Valor Merkadu:", `USD $${Number(i.estimated_value || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`],
                ].filter(([, a, , b]) => a || b).map(([l1, v1, l2, v2], r) => (
                  <tr key={r} className="border-b border-slate-100">
                    <td className="py-1 pl-2 font-bold text-slate-600 w-[16%]">{l1}</td>
                    <td className="py-1 w-[34%]">{v1 || "—"}</td>
                    <td className="py-1 font-bold text-slate-600 w-[16%]">{l2}</td>
                    <td className="py-1">{v2 || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>
      )}
      <DocFooter shop={receipt.shop} />
    </div>
  );
}


export function InvoiceDoc({ invoice, printMode = "a4" }) {
  const { t } = useLang();
  const s = invoice.sale || {};
  const snap = s.due_snapshot || {};
  const loan = invoice.loan;
  const item = invoice.item || {};
  return (
    <div className={`print-area bg-white text-sm ${printMode === "thermal" ? "thermal-doc" : ""}`} data-testid="invoice-body">
      <DocHeader shop={invoice.shop} testid="invoice-logo" />
      <h2 className="text-center font-heading font-bold text-xl tracking-wide my-4">{t("inv.title")}</h2>
      <table className="w-full text-xs border border-slate-300 mb-3">
        <tbody>
          <tr className="border-b border-slate-200">
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50 w-[18%]">{t("inv.no")}</td>
            <td className="p-2 w-[32%] font-mono" data-testid="invoice-no">{s.invoice_no}</td>
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50 w-[18%]">{t("rcp.contract_no")}</td>
            <td className="p-2 font-mono">{loan ? `CTR-${loan.start_date.slice(0, 4)}-${loan.loan_number.replace("LN-", "")}` : "—"}</td>
          </tr>
          <tr className="border-b border-slate-200">
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50">{t("rcp.client")}</td>
            <td className="p-2">{invoice.customer?.name || s.customer_name || "—"}</td>
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50">{t("inv.buyer")}</td>
            <td className="p-2" data-testid="invoice-buyer">{s.buyer_name || "—"}</td>
          </tr>
          <tr className="border-b border-slate-200">
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50">{t("common.date")}</td>
            <td className="p-2 font-mono">{fmtDate(s.sold_at)}</td>
            <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50">{t("inv.sold_by")}</td>
            <td className="p-2">{s.created_by || "—"}</td>
          </tr>
        </tbody>
      </table>
      <div className="text-xs font-bold text-slate-700 mb-1">{t("inv.item_sold")}</div>
      <table className="w-full text-[10px] sm:text-xs border border-slate-300 mb-4">
        <tbody>
          <tr className="border-b border-slate-200">
            <td className="p-2">{item.name || s.item_name}</td>
            <td className="p-2">{item.category || "—"}</td>
            <td className="p-2 font-mono">{item.serial_no || item.plate_no || "—"}</td>
            <td className="p-2 text-right font-mono">{money(item.estimated_value)}</td>
          </tr>
        </tbody>
      </table>
      <div className="text-xs font-bold text-slate-700 mb-1">{t("inv.allocation")}</div>
      <table className="w-full text-[10px] sm:text-xs" data-testid="invoice-allocation">
        <tbody>
          {[
            [t("inv.principal"), snap.principal_due],
            [t("inv.interest"), snap.interest_due],
            [t("inv.penalty"), snap.penalty],
          ].map(([l, v], r) => (
            <tr key={r} className="border-b border-slate-100">
              <td className="py-1.5 pl-2 text-slate-600">{l}</td>
              <td className="py-1.5 text-right pr-2 font-mono">{money(v)}</td>
            </tr>
          ))}
          <tr className="border-b-2 border-slate-800 font-bold">
            <td className="py-2 pl-2">{t("inv.total_debt")}</td>
            <td className="py-2 text-right pr-2 font-mono" data-testid="invoice-total-debt">{money(snap.total_due)}</td>
          </tr>
          <tr className="border-b border-slate-100 font-semibold">
            <td className="py-1.5 pl-2">{t("inv.sale_price")}</td>
            <td className="py-1.5 text-right pr-2 font-mono" data-testid="invoice-sale-price">{money(s.sale_price)}</td>
          </tr>
          {s.surplus > 0 && (
            <tr className="border-b border-slate-100">
              <td className="py-1.5 pl-2 text-slate-600">{t("inv.surplus")}</td>
              <td className="py-1.5 text-right pr-2 font-mono">{money(s.surplus)}</td>
            </tr>
          )}
        </tbody>
      </table>
      <div className={`border rounded-lg p-3 mt-4 text-xs ${s.loss > 0 ? "bg-red-50 border-red-300" : "bg-emerald-50 border-emerald-300"}`} data-testid="invoice-result-box">
        <div className="flex justify-between font-bold text-sm">
          <span>{t("inv.profit")}</span>
          <span className="font-mono" data-testid="invoice-profit">{money(s.profit)}</span>
        </div>
        {s.loss > 0 && (
          <div className="flex justify-between font-bold text-sm text-red-700 mt-1">
            <span>{t("inv.loss")}</span>
            <span className="font-mono" data-testid="invoice-loss">{money(s.loss)}</span>
          </div>
        )}
        <p className="text-[10px] italic text-slate-500 mt-1.5">{t("inv.to_finance")}</p>
      </div>
      {s.winning_bid && (
        <div className="border border-purple-300 bg-purple-50 rounded-lg p-3 mt-3 text-xs" data-testid="invoice-winning-bid">
          <div className="font-bold text-purple-900 mb-1">{t("inv.winning_bid")}</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
            <span className="text-slate-600">{t("bid.name")}</span><span>{s.winning_bid.bidder_name}{s.winning_bid.bidder_id ? ` · ID ${s.winning_bid.bidder_id}` : ""}{s.winning_bid.phone ? ` · ${s.winning_bid.phone}` : ""}</span>
            <span className="text-slate-600">{t("bid.amount")}</span><span className="font-mono font-semibold">{money(s.winning_bid.amount)}</span>
            <span className="text-slate-600">{t("ann.reserve")}</span><span className="font-mono">{s.winning_bid.reserve_price != null ? money(s.winning_bid.reserve_price) : "—"}</span>
            <span className="text-slate-600">{t("inv.bids_count")}</span><span>{s.winning_bid.bids_count}</span>
          </div>
        </div>
      )}
      {invoice.pickup_terms && (
        <div className="mt-3 text-[11px] leading-relaxed text-slate-700 border-t border-dashed border-slate-300 pt-2" data-testid="invoice-pickup-terms">
          <span className="font-bold">{t("inv.pickup_terms")}: </span>{invoice.pickup_terms}
        </div>
      )}
      <div className="grid grid-cols-2 gap-8 mt-12 text-center text-xs">
        <div>
          <div className="border-t border-slate-500 pt-2 font-semibold">{invoice.shop.name}</div>
          <div className="text-slate-600 mt-1">{t("inv.sold_by")}</div>
        </div>
        <div>
          <div className="border-t border-slate-500 pt-2 font-semibold">{t("inv.buyer")}</div>
          <div className="text-slate-600 mt-1">{s.buyer_name || "____________________"}</div>
        </div>
      </div>
      <DocFooter shop={invoice.shop} />
    </div>
  );
}

export function LetterDoc({ doc, printMode = "a4", draftMark = false, testid = "letter-body" }) {
  const { t } = useLang();
  const c = doc.customer || {};
  const isCompany = doc.parties?.borrower_kind === "company" || c.customer_kind === "company";
  return (
    <div className={`print-area bg-white text-sm ${printMode === "thermal" ? "thermal-doc" : ""}`} data-testid={testid}>
      {draftMark && <div className="draft-watermark">DRAFT</div>}
      <DocHeader shop={doc.shop} testid={`${testid}-logo`} />
      <div className="flex justify-between text-xs mt-3 font-mono">
        <span data-testid={`${testid}-no`}>{doc.notice_no || doc.addendum_no}</span>
        <span>{doc.date || doc.meta?.date}</span>
      </div>
      <div className="mt-3 text-xs border border-slate-200 rounded p-2" data-testid={`${testid}-recipient`}>
        <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--brand,#1E3A5F)]">{t("doc.client")}</div>
        <div>{c.name}{c.id_number ? ` · ${c.id_type || "BI"} ${c.id_number}` : ""}{c.nif ? ` · NIF ${c.nif}` : ""}</div>
        {c.address && <div>{[c.address, c.suco, c.posto, c.municipality].filter(Boolean).join(", ")}</div>}
        <div>{c.phone && `Tel./WhatsApp ${c.phone}`}{c.email && ` · ${c.email}`}</div>
        <div className="mt-1 text-slate-500">{t("rcp.contract_no")}: <span className="font-mono">{doc.meta?.contract_no}</span> · {doc.loan?.loan_number}</div>
      </div>
      {doc.sections.map((s, i) => (
        <div key={s.lang} className={i === 0 ? "mt-4" : "mt-4 pt-3 border-t border-dashed border-slate-300"} data-testid={`${testid}-section-${s.lang}`}>
          <h3 className="font-heading font-bold text-sm text-[var(--brand,#1E3A5F)]">{s.title}</h3>
          <p className="whitespace-pre-line text-[12px] leading-relaxed mt-1">{s.body}</p>
        </div>
      ))}
      {doc.deadline && (
        <div className="mt-4 text-xs font-semibold border border-slate-300 rounded p-2 inline-block" data-testid={`${testid}-deadline`}>
          {t("ntc.deadline")}: <span className="font-mono">{doc.deadline}</span>
        </div>
      )}
      <div className={`grid gap-6 mt-12 text-center text-xs ${doc.addendum_no ? "grid-cols-3" : "grid-cols-2"}`}>
        <div>
          <div className="border-t border-slate-500 pt-2 font-semibold">{t("doc.sign_creditor")}</div>
          <div className="text-slate-600 mt-1">{doc.shop.name}</div>
        </div>
        <div>
          <div className="border-t border-slate-500 pt-2 font-semibold">{doc.addendum_no ? t("doc.sign_client") : t("ntc.received_by")}{isCompany ? ` · ${t("ct.company")}` : ""}</div>
          <div className="text-slate-600 mt-1">{isCompany && c.rep_name ? `${c.rep_name} p/ ${c.name}` : c.name}</div>
        </div>
        {doc.addendum_no && (
          <div>
            <div className="border-t border-slate-500 pt-2 font-semibold">{t("doc.sign_witness")}</div>
            <div className="text-slate-600 mt-1">{doc.parties?.witness_name || "____________________"}</div>
          </div>
        )}
      </div>
      <DocFooter shop={doc.shop} />
    </div>
  );
}
